import i18n from "../../i18n";
import { loadAppStringsForLanguage } from "./loadAppStrings";
import { canonicalizeLanguageTag } from "./supportedLanguages";

/**
 * Maps API `app_currency_config.platform_language` to an i18next `lng`.
 * API sends short codes, e.g. `"en"`, `"es"` (also supports `"de"`, `"pt-BR"` / `pt-br`).
 * Missing or malformed values clamp to `"en"` (the platform default path).
 */
export function normalizePlatformLanguageToI18nLng(raw) {
  return canonicalizeLanguageTag(raw) ?? "en";
}

/**
 * Switches the i18next language and loads the matching TCM V2 strings for it.
 * Never throws: an i18next failure falls back to `en`, a strings-fetch failure
 * keeps whatever strings are already loaded.
 */
export async function applyI18nLanguage(lng) {
  // Track the effective language so the strings we load stay in sync with the
  // language i18next actually settled on (it falls back to "en" on failure).
  let effectiveLng = lng;
  try {
    await i18n.changeLanguage(lng);
  } catch {
    effectiveLng = "en";
    await i18n.changeLanguage("en");
  }
  try {
    await loadAppStringsForLanguage(effectiveLng);
  } catch (error) {
    // Keep the strings already loaded at boot if the per-language fetch fails.
    console.warn(
      `Failed to load TCM strings for "${effectiveLng}"; keeping current strings.`,
      error,
    );
  }
}

/**
 * Call after platform config is loaded (e.g. setAppConfig payload).
 * Applies the client-level `platform_language` only. App entry points should
 * prefer `applyResolvedI18nLanguage` (utils/i18n/languagePreference), which
 * layers the admin/guest preference on top of this when present.
 */
export async function applyI18nLanguageFromAppConfig(appConfig) {
  const raw = appConfig?.app_currency_config?.platform_language;
  await applyI18nLanguage(normalizePlatformLanguageToI18nLng(raw));
}
